import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from 'components/common/IconButton';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useState } from 'react';
import { Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import 'react-loading-skeleton/dist/skeleton.css';

import DatosFiscalesReceptor from '../facturas/DatosFiscalesReceptor';
import SelectorEmisor from '../facturas/SelectorEmisor';
import PreferenciasComplementoPago from './PreferenciasComplementoPago';

import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import InformacionGeneralComplemento from './InformacionGeneralComplemento';
import DocumentoRelacionado from './DocumentoRelacionado';

const GenerarComplementoPago = ({
    show,
    onHide,
    idPago, 
    selectedFactura,
    orden
}) => {

    const { register, setValue, getValues } = useForm();
    const { showSuccessToast, showErrorToast } = useToast();
    const { token, setToken } = useToken();
    const navigate = useNavigate();

    //estado para cuando se envia a timbrar
    const [facturandoTimbrando, setFacturandoTimbrando] = useState(false);

    const [idEmisorSeleccionado, setIdEmisorSeleccionado] = useState("");
    const [rfcSeleccionado, setRFCSeleccionado] = useState("");

    // Estado para almacenar los impuestos de la factura
    const [impuestosFactura, setImpuestosFactura] = useState(null);

    const [selectedValuesDDL, setSelectedValuesDDL] = useState({
        vRFC: "",
        vCodigoRegimen: "",
        vMoneda: "",
        vFormaPago: "",
        vMetodoPagoDR: "",
        vObjetoImpDR: "",
        vSerie: "",
        vPais: ""
    });

    const now = new Date();
    const minDate = new Date(now.getTime() - 70 * 60 * 60 * 1000);
    const [errors, setErrors] = useState({});
    const [isForeignRFC, setIsForeignRFC] = useState(false);

    useEffect(() => {
        if (orden) {
            // Load data from orden
            const rfcOrden = orden.vRfc || '';
            setValue('vRFC', rfcOrden);
            setValue('vRazonSocial', orden.vRazonSocial || '');
            setValue('vCP', orden.vCp || '');
            setValue('vEmail', orden.vEmailFacturacion || '');
            // Set other values as needed
            setSelectedValuesDDL(prev => ({
                ...prev,
                vRFC: rfcOrden,
                vCodigoRegimen: orden.vCodigoRegimen || '',
                vPais: orden.vPaisFacturacion || '',
                vSerie: prev.vSerie || 'P'
            }));

            // Detectar si es RFC extranjero
            if (rfcOrden === 'XEXX010101000') {
                setIsForeignRFC(true);
            }
        }

    }, [idPago]);

    /**
     * Cargar impuestos de la factura relacionada
     */
    useEffect(() => {
        const cargarImpuestosFactura = async () => {
            if (!selectedFactura || !selectedFactura.vUUIDFactura) {
                return;
            }

            try {
                const response = await fetch(`/api/Facturas/obtenerDetalleFactura?uuid=${selectedFactura.vUUIDFactura}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const facturaData = await response.json();
                    
                    // Parsear el XML de la factura para extraer impuestos
                    if (facturaData.vXML) {
                        let xmlData;
                        try {
                            xmlData = typeof facturaData.vXML === 'string' 
                                ? JSON.parse(facturaData.vXML) 
                                : facturaData.vXML;
                        } catch (e) {
                            console.error('Error parsing XML:', e);
                            return;
                        }

                        const comprobante = xmlData?.Comprobante;
                        if (comprobante && comprobante.Impuestos) {
                            const impuestos = comprobante.Impuestos;
                            
                            // Procesar traslados
                            const trasladosDR = [];
                            const traslados = impuestos.Traslados?.Traslado;
                            
                            if (traslados) {
                                const trasladosArray = Array.isArray(traslados) ? traslados : [traslados];
                                
                                trasladosArray.forEach((traslado) => {
                                    trasladosDR.push({
                                        BaseOriginal: parseFloat(traslado.Base || "0"),
                                        ImpuestoDR: traslado.Impuesto || "002",
                                        TipoFactorDR: traslado.TipoFactor || "Tasa",
                                        TasaOCuotaDR: traslado.TasaOCuota || "0.000000",
                                        ImporteOriginal: parseFloat(traslado.Importe || "0")
                                    });
                                });
                            }

                            // Procesar retenciones
                            const retencionesDR = [];
                            const retenciones = impuestos.Retenciones?.Retencion;
                            
                            if (retenciones) {
                                const retencionesArray = Array.isArray(retenciones) ? retenciones : [retenciones];
                                
                                retencionesArray.forEach((retencion) => {
                                    retencionesDR.push({
                                        BaseOriginal: parseFloat(retencion.Base || "0"),
                                        ImpuestoDR: retencion.Impuesto || "002",
                                        TipoFactorDR: retencion.TipoFactor || "Tasa",
                                        TasaOCuotaDR: retencion.TasaOCuota || "0.000000",
                                        ImporteOriginal: parseFloat(retencion.Importe || "0")
                                    });
                                });
                            }

                            // Guardar impuestos si hay datos (con valores originales)
                            if (trasladosDR.length > 0 || retencionesDR.length > 0) {
                                setImpuestosFactura({
                                    TrasladosDR: trasladosDR.length > 0 ? trasladosDR : null,
                                    RetencionesDR: retencionesDR.length > 0 ? retencionesDR : null,
                                    TotalFactura: parseFloat(comprobante.Total || "0")
                                });
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error al cargar impuestos de la factura:', error);
            }
        };

        cargarImpuestosFactura();
    }, [selectedFactura]);

    /**
     * Cambios de RFC
     */
    const handleRFCChange = (rfc) => {
        rfc = rfc.toUpperCase().trim();

        setValue("vRFC", rfc, { shouldValidate: true });

        // Actualizar selectedValues con el nuevo RFC para que DatosFiscalesReceptor lo detecte
        setSelectedValuesDDL((prev) => ({ ...prev, vRFC: rfc }));

        // Detectar si es RFC extranjero
        if (rfc === 'XEXX010101000') {
            setIsForeignRFC(true);
        } else {
            setIsForeignRFC(false);
            // Limpiar el valor del pa�s si no es RFC extranjero
            setSelectedValuesDDL((prev) => ({ ...prev, vPais: '' }));
        }
    }

    /**
     * Limpia Errores 
     */
    const clearError = (fieldName) => {
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[fieldName];
            return newErrors;
        });
    }

    const validarDatosComplemento = (data) => {
        let newErrors = {};

        // Validar emisor seleccionado
        if (!idEmisorSeleccionado) {
            newErrors.idEmisor = "Debe seleccionar un Emisor";
        }
      //  alert(data["vFormaPago"]);
        // Lista de campos obligatorios
        const camposObligatorios = [
            { key: 'vCodigoRegimen', mensaje: 'El campo R�gimen Fiscal es requerido' },
            { key: 'vMoneda', mensaje: 'El campo Moneda es requerido' },
            { key: 'vCP', mensaje: 'El campo C�digo Postal es requerido' },
            { key: 'vRFC', mensaje: 'El campo RFC es requerido' },
            { key: 'vRazonSocial', mensaje: 'El campo Raz�n Social es requerido' },
            { key: 'dSaldoAnterior', mensaje: 'El campo Saldo Anterior es requerido' },
            { key: 'dSaldoInsoluto', mensaje: 'El campo Saldo Insoluto es requerido' },
            { key: 'dImportePago', mensaje: 'El campo Importe del Pago es requerido' },
            { key: 'iNumeroParcialidad', mensaje: 'El campo N�mero de Parcialidad es requerido' },
            { key: 'dFechaPago', mensaje: 'El campo Fecha del Pago es requerido' },
            { key: 'vFormaPago', mensaje: 'El campo Forma de Pago es requerido' },
            { key: 'dMonto', mensaje: 'El campo Monto es requerido' },
            { key: 'vMetodoPagoDR', mensaje: 'El campo M�todo de Pago DR es requerido' },
            { key: 'vObjetoImpDR', mensaje: 'El campo Objeto Impuesto DR es requerido' },
            { key: 'vIdDocumento', mensaje: 'El campo ID Documento es requerido' },
            { key: 'vSerie', mensaje: 'El campo Serie es requerido' },
            { key: 'vFolio', mensaje: 'El campo Folio es requerido' },
        ];

        // Verificar campos obligatorios
        camposObligatorios.forEach(({ key, mensaje }) => {
            if (!data[key]) {
                newErrors[key] = mensaje;
            }
        });

        // Validar tipo de cambio solo si la moneda NO es MXN
        if (data.vMoneda && data.vMoneda !== "MXN") {
            if (!data.dTipoCambio || data.dTipoCambio <= 0) {
                newErrors.dTipoCambio = "El tipo de cambio es obligatorio para monedas distintas a MXN";
            }
        }

        // Validar pa�s si es RFC extranjero
        if (isForeignRFC && !data.vPais) {
            newErrors.vPais = "El campo Pa�s es requerido para RFC extranjero";
        }

        // Validaci�n espec�fica para C�digo Postal (vCP)
        if (data.vCP && !/^\d{5}$/.test(data.vCP)) {
            newErrors.vCP = "El c�digo postal debe tener 5 d�gitos num�ricos";
        }

        // Validaci�n del email
        if (data.vEmail && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(data.vEmail)) {
            newErrors.vEmail = "Formato de correo no v�lido";
        }

        // Validar que los importes sean positivos
        if (data.dSaldoAnterior && parseFloat(data.dSaldoAnterior) < 0) {
            newErrors.dSaldoAnterior = "El saldo anterior debe ser un n�mero positivo";
        }
        if (data.dSaldoInsoluto && parseFloat(data.dSaldoInsoluto) < 0) {
            newErrors.dSaldoInsoluto = "El saldo insoluto debe ser un n�mero positivo";
        }
        if (data.dImportePago && parseFloat(data.dImportePago) < 0) {
            newErrors.dImportePago = "El importe del pago debe ser un n�mero positivo";
        }
        if (data.dMonto && parseFloat(data.dMonto) < 0) {
            newErrors.dMonto = "El monto debe ser un n�mero positivo";
        }
        if (data.iNumeroParcialidad && (parseInt(data.iNumeroParcialidad) <= 0 || !Number.isInteger(parseFloat(data.iNumeroParcialidad)))) {
            newErrors.iNumeroParcialidad = "El n�mero de parcialidad debe ser un entero positivo";
        }

        return newErrors;
    }

    const handleSubmit = (e = null) => {
        if (e) e.preventDefault();

        // Obt�n los valores del formulario registrados con react-hook-form
        const formData = getValues();
        // Combina los valores seleccionados manualmente con los del formulario
        const data = { ...selectedValuesDDL, ...formData };

        
        // Validar antes de enviar
        const errores = validarDatosComplemento(data);
        if (Object.keys(errores).length > 0) {
            setErrors(errores); // Establece los errores en el estado
            const errorMessages = Object.values(errores).join(', ');
            showErrorToast(`Errores de validaci�n: ${errorMessages}`);
            return; // Detiene la ejecuci�n si hay errores
        }

        // Si no hay errores, procede con el registro
        registrarComplementoPago(data);
    }

    /**
    * Registrar complemento de pago
    */
    const registrarComplementoPago = async (data) => {
        setFacturandoTimbrando(true);
        
        try {
            // Calcular impuestos proporcionales basados en el importe pagado
            let impuestosDRProporcionales = null;
            
            if (impuestosFactura && data.dImportePago && impuestosFactura.TotalFactura > 0) {
                const importePagado = parseFloat(data.dImportePago);
                const totalFactura = impuestosFactura.TotalFactura;
                const proporcion = importePagado / totalFactura;

                impuestosDRProporcionales = {};

                // Calcular traslados proporcionales
                if (impuestosFactura.TrasladosDR) {
                    impuestosDRProporcionales.TrasladosDR = impuestosFactura.TrasladosDR.map(traslado => {
                        const baseDR = traslado.BaseOriginal * proporcion;
                        const importeDR = traslado.ImporteOriginal * proporcion;
                        
                        return {
                            BaseDR: baseDR.toFixed(2),
                            ImpuestoDR: traslado.ImpuestoDR,
                            TipoFactorDR: traslado.TipoFactorDR,
                            TasaOCuotaDR: traslado.TasaOCuotaDR,
                            ImporteDR: importeDR.toFixed(2)
                        };
                    });
                }

                // Calcular retenciones proporcionales
                if (impuestosFactura.RetencionesDR) {
                    impuestosDRProporcionales.RetencionesDR = impuestosFactura.RetencionesDR.map(retencion => {
                        const baseDR = retencion.BaseOriginal * proporcion;
                        const importeDR = retencion.ImporteOriginal * proporcion;
                        
                        return {
                            BaseDR: baseDR.toFixed(2),
                            ImpuestoDR: retencion.ImpuestoDR,
                            TipoFactorDR: retencion.TipoFactorDR,
                            TasaOCuotaDR: retencion.TasaOCuotaDR,
                            ImporteDR: importeDR.toFixed(2)
                        };
                    });
                }
            }

            // Estructurar los datos en el formato requerido por el backend
            const payload = {
                data: {
                    // Datos del Comprobante
                    Comprobante: {
                        Serie: data.vSerie,
                        Folio: data.vFolio,
                        FechaEmision: data.dFechaEmision
                    },
                    
                    // Datos del Receptor
                    Receptor: {
                        Rfc: data.vRFC,
                        Nombre: data.vRazonSocial,
                        DomicilioFiscalReceptor: data.vCP,
                        RegimenFiscalReceptor: data.vCodigoRegimen,
                        Email: data.vEmail || undefined,
                        ResidenciaFiscal: isForeignRFC ? data.vPais : undefined,
                        NumRegTrib: isForeignRFC ? data.vNumRegTrib : undefined
                    },
                    
                    // Datos del Pago
                    Pago: {
                        FechaPago: data.dFechaPago,
                        FormaDePagoP: data.vFormaPago,
                        MonedaP: data.vMoneda,
                        Monto: parseFloat(data.dMonto),
                        TipoCambioP: data.vMoneda !== "MXN" ? parseFloat(data.dTipoCambio) : 1,
                        NumOperacion: data.vNumOperacion || undefined
                    },
                    
                    // Datos del Documento Relacionado
                    DoctoRelacionado: [{
                        IdDocumento: data.vIdDocumento, // UUID de la factura relacionada
                        Serie: selectedFactura?.vSerie,
                        Folio: selectedFactura?.vFolio,
                        MonedaDR: selectedFactura.vMoneda,
                        EquivalenciaDR: 1,
                        NumParcialidad: parseInt(data.iNumeroParcialidad),
                        ImpSaldoAnt: parseFloat(data.dSaldoAnterior),
                        ImpPagado: parseFloat(data.dImportePago),
                        ImpSaldoInsoluto: parseFloat(data.dSaldoInsoluto),
                        ObjetoImpDR: data.vObjetoImpDR,
                        // Incluir impuestos proporcionales calculados
                        ImpuestosDR: impuestosDRProporcionales
                    }]
                },
                idEmisor: parseInt(idEmisorSeleccionado),
                IdPago: idPago // Agregar idPago para vincular con tblPago
            };

            console.log('Payload con impuestos proporcionales:', JSON.stringify(payload, null, 2));

            const response = await fetch("/api/Facturas/registrarComplementoPago", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const responseData = await response.json();

                if (responseData.iResponseType === 1) {
                    showSuccessToast(responseData.vMessage || 'Complemento de pago generado exitosamente');
                    onHide();
                    
                    // Redirigir a la URL proporcionada por el backend
                    if (responseData.redirectUrl) {
                        navigate(responseData.redirectUrl);
                    }
                } else {
                    showErrorToast(responseData.vMessage || "Error al procesar el complemento de pago");
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                showErrorToast(errorData.vMessage || `Error al procesar el complemento de pago: ${response.status}`);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la petici�n: ' + error.message);
        } finally {
            setFacturandoTimbrando(false);
        }
    };

    /**
     * Fecha valores e icono 
     */
    const GreetingsDateInput = forwardRef(({ value, onClick }, ref) => (
        <div className="position-relative">
            <Form.Control
                size="sm"
                ref={ref}
                onClick={onClick}
                value={value || ""} // Si value es null o undefined, muestra una cadena vac�a
                placeholder="Seleccione una fecha" // Muestra el texto cuando no hay valor
                className="ps-4"
                readOnly // Evita edici�n manual del campo
            />
            <FontAwesomeIcon
                icon="calendar-alt"
                className="text-primary position-absolute top-50 translate-middle-y ms-2"
            />
        </div>
    ));

    GreetingsDateInput.propTypes = {
        value: PropTypes.string,
        onClick: PropTypes.func,
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>Generar Complemento de Pago</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Card className="shadow-none border mb-3">
                        <Card.Body>
                            <Row>
                                <Col xl={6} style={{ borderRight: "1px solid #ddd", paddingRight: "15px" }}>
                                    <h5>Datos Fiscales del Receptor</h5>
                                    {/* Selector de Emisor */}
                                    <SelectorEmisor
                                        idEmisorSeleccionado={idEmisorSeleccionado}
                                        setIdEmisorSeleccionado={setIdEmisorSeleccionado}
                                        cargandoForm={false}
                                        errors={errors}
                                        clearError={clearError}
                                        setRFCSeleccionado={setRFCSeleccionado}
                                    />

                                    {/* Secci�n: Datos Fiscales */}
                                    <DatosFiscalesReceptor
                                        cargandoForm={false}
                                        errors={errors}
                                        clearError={clearError}

                                        // Props de datos fiscales del receptor
                                        register={register}
                                        selectedValues={selectedValuesDDL}
                                        setSelectedValues={setSelectedValuesDDL}
                                        handleRFCChange={handleRFCChange}
                                        setValue={setValue}
                                    />

                                    <InformacionGeneralComplemento
                                        register={register}
                                        selectedValuesDDL={selectedValuesDDL}
                                        setSelectedValuesDDL={setSelectedValuesDDL}
                                        errors={errors}
                                        clearError={clearError}
                                        setValue={setValue}
                                        now={now}
                                        minDate={minDate}
                                    />
                                </Col>
                                {/* Secci�n: Preferencias de Facturaci�n */}
                                <Col xl={6}>
                                    <PreferenciasComplementoPago
                                        register={register}
                                        selectedValuesDDL={selectedValuesDDL}
                                        setSelectedValuesDDL={setSelectedValuesDDL}
                                        errors={errors}
                                        clearError={clearError}
                                        setValue={setValue}
                                        now={now}
                                        minDate={minDate}
                                        idPago={idPago}
                                        vUUIDFactura={selectedFactura?.vUUIDFactura }
                                        rfcSeleccionado={rfcSeleccionado}
                                    />
                                    <DocumentoRelacionado
                                        register={register}
                                        selectedValuesDDL={selectedValuesDDL}
                                        setSelectedValuesDDL={setSelectedValuesDDL}
                                        errors={errors}
                                        clearError={clearError}
                                        selectedFactura={selectedFactura}
                                    />
                                </Col>
                            </Row>

                            <Row className="justify-content-end mt-3">
                                {
                                    facturandoTimbrando ?
                                        <>
                                            <Col className="col-auto">
                                                <IconButton
                                                    variant="falcon-default"
                                                    size="sm"
                                                    transform="shrink-2"
                                                    className="d-sm-block me-2"
                                                    disabled
                                                >
                                                    <Spinner className="text-inline" animation="border" size="sm" variant="ligth" />&nbsp; Cargando...
                                                </IconButton>
                                            </Col>
                                        </>
                                        :
                                        <>
                                            <Col className="col-auto d-flex align-items-center">
                                                {/* Bot�n "Generar Complemento" */}
                                                <IconButton
                                                    variant="falcon-default"
                                                    size="sm"
                                                    icon="check"
                                                    transform="shrink-2"
                                                    type="submit"
                                                    className="me-2"
                                                >
                                                    <span className=" ms-1">Generar Complemento</span>
                                                </IconButton>

                                                {/* Bot�n "Cancelar" */}
                                                <IconButton
                                                    onClick={onHide}
                                                    icon="xmark"
                                                    variant="falcon-danger"
                                                    size="sm"
                                                    transform="shrink-2"
                                                >
                                                    <span className="ms-1">Cancelar</span>
                                                </IconButton>
                                            </Col>
                                        </>
                                }
                            </Row>
                        </Card.Body>
                    </Card>
                </Form>

            </Modal.Body>
        </Modal>
    );
};

GenerarComplementoPago.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    idPago: PropTypes.number.isRequired,
    idOrden: PropTypes.number.isRequired,
    selectedFactura: PropTypes.object,
    orden: PropTypes.object.isRequired
};

export default GenerarComplementoPago;